import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BackgroundServiceResponseModel } from "cpl-react-common/components/ActivityIndicators/BackgroundProcess/BackgroundServiceResponseModel";
import { BackgroundServiceServiceType } from "cpl-react-common/components/Enums/BackgroundServiceServiceType";
import { BackgroundServiceStatusType } from "cpl-react-common/components/Enums/BackgroundServiceStatusType";

import { toast } from "react-toastify";
import { IBackgroundServiceService } from "services/BackgroundServiceService";
import { RootState } from "setup";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";

type BackgroundActivityStateType = {
  HasNewUpdate: boolean;
  CurrentProcesses: BackgroundServiceResponseModel[];
  ErrorProcess: BackgroundServiceResponseModel | undefined;
  ShowError: boolean;
};

const initialState: BackgroundActivityStateType = {
  HasNewUpdate: false,
  CurrentProcesses: new Array<BackgroundServiceResponseModel>(),
  ErrorProcess: undefined,
  ShowError: false,
};

export const backgroundActivitySlice = createSlice({
  name: "backgroundActivity",
  initialState,
  reducers: {
    setHasNewUpdate: (state, action) => {
      state.HasNewUpdate = action.payload;
    },
    setCurrentProcesses: (state, action) => {
      state.CurrentProcesses = action.payload;
    },
    setShowError: (state, action) => {
      state.ShowError = action.payload;
    },
    handleResponseModel: (state, action) => {
      const response: BackgroundServiceResponseModel = action.payload;
      if (response.serviceType == BackgroundServiceServiceType.ServiceChargeInvoicingPreList) {
        //User can only have one ServiceChargeInvoice process running at a time, so remove all previous processes in state
        state.CurrentProcesses = state.CurrentProcesses.filter(
          (i) => i.serviceType != BackgroundServiceServiceType.ServiceChargeInvoicingPreList
        );
      }
      const exists = state.CurrentProcesses.some((process) => {
        return process.processID === response.processID;
      });
      if (exists) {
        state.CurrentProcesses = state.CurrentProcesses.map((process) => {
          if (process.processID == response.processID) {
            return response;
          } else {
            return process;
          }
        });
      } else {
        state.CurrentProcesses.push(response);
      }
      state.HasNewUpdate = true;
      if (response.statusType === BackgroundServiceStatusType.Completed) {
        toast.success(response.message ?? "Process complete");
      } else if (response.statusType === BackgroundServiceStatusType.Failed) {
        state.ErrorProcess = response;
        state.ShowError = true;
      }
    },
  },
});

export const acknowledgeProcess = createAsyncThunk(
  "backgroundActivity/AcknowledgeProcess",
  async (processID: number) => {
    const backgroundServiceService = container.get<IBackgroundServiceService>(SERVICE_TYPES.BackgroundServiceService);
    return await backgroundServiceService.acknowledge(processID);
  }
);

export const notifyAllActiveProcesses = createAsyncThunk("backgroundActivity/NotifyAllActiveProcesses", async () => {
  const backgroundServiceService = container.get<IBackgroundServiceService>(SERVICE_TYPES.BackgroundServiceService);
  return await backgroundServiceService.notifyAllActiveProcesses();
});

export const selectNewUpdate = (state: RootState) => {
  return state.backgroundActivities.HasNewUpdate;
};

export const selectProcessProgress = (processID: number) => (state: RootState) =>
  state.backgroundActivities.CurrentProcesses.find((i) => i.processID === processID);

export const selectAllProcesses = (state: RootState) => state.backgroundActivities.CurrentProcesses;
export const selectErrorProcess = (state: RootState) => state.backgroundActivities.ErrorProcess;
export const selectShowError = (state: RootState) => state.backgroundActivities.ShowError;

export const { handleResponseModel, setHasNewUpdate, setCurrentProcesses, setShowError } =
  backgroundActivitySlice.actions;

export default backgroundActivitySlice.reducer;
